/**
 * Intent-based Hybrid Context Router for GitHub repo chat (Phase 1).
 *
 * Routes user messages to RAG-only, RAG + full-file fetch, or full-file-required modes.
 * Uses a pluggable resolver chain (explicit path → filename → symbol FTS → RAG vector).
 */

import { supabase, getCurrentUserId, isSupabaseConfigured } from '@/lib/supabase';
import * as githubService from '@/services/githubService';
import {
  searchRepo,
  reconstructFilesFromChunks,
  isRepoFullyIndexed,
  type RepoChunkResult,
} from '@/services/repoRagService';
import { getGraphNeighborPaths } from '@/services/repoGraphService';

const MAX_FULL_FILES = 5;
const MAX_FULL_FILE_BYTES = 50_000;

export type RepoContextMode =
  | 'rag_only'
  | 'rag_plus_full_file'
  | 'full_file_required'
  | 'clarification_required';

export interface RepoContextDecision {
  mode: RepoContextMode;
  isEditIntent: boolean;
  hasFileReference: boolean;
  hasSymbolReference: boolean;
  reason: string;
}

export type ResolvedPathSource =
  | 'explicit_path'
  | 'filename'
  | 'symbol_text'
  | 'rag_vector'
  | 'ast_symbol'
  | 'graph';

export interface ResolvedPathCandidate {
  path: string;
  source: ResolvedPathSource;
  confidence: number;
}

export interface ResolvedFullFile {
  path: string;
  content: string;
  truncated: boolean;
  fromIndex?: boolean;
}

export interface RepoResolverInput {
  message: string;
  owner: string;
  repo: string;
  repoTreePaths: string[];
  ragResults: RepoChunkResult[];
}

export interface RepoResolver {
  name: string;
  resolve(input: RepoResolverInput): Promise<ResolvedPathCandidate[]>;
}

export interface RepoContextBuildInput {
  message: string;
  owner: string;
  repo: string;
  repoTreePaths: string[];
  ref?: string;
}

export interface RepoContextBuildResult {
  decision: RepoContextDecision;
  ragResults: RepoChunkResult[];
  candidates: ResolvedPathCandidate[];
  resolvedPaths: string[];
  graphExpandedPaths: string[];
  fullFiles: ResolvedFullFile[];
  allowRepoEdit: boolean;
  /** Top intent-scored target for edit planning */
  primaryIntentPath?: string;
}

const EDIT_INTENT_WORDS = [
  'edit', 'update', 'change', 'modify', 'fix', 'refactor', 'improve',
  'add', 'remove', 'delete', 'implement', 'rewrite', 'patch', 'create',
];

const FILE_EXT_RE =
  /\b[\w.-]+\.(ts|tsx|js|jsx|mjs|cjs|py|go|rs|java|php|rb|cs|cpp|c|h|json|md|mdx|yml|yaml|sql|css|scss|html)\b/i;

const PATH_RE = /[\w.-]+(?:\/[\w.-]+)+\.\w+/g;

export function detectFileReference(message: string): boolean {
  return PATH_RE.test(message) || FILE_EXT_RE.test(message);
}

const STOP_WORDS = new Set([
  'the', 'this', 'that', 'with', 'from', 'into', 'your', 'please', 'help',
  'file', 'code', 'repo', 'repository', 'function', 'class', 'method',
  'update', 'change', 'modify', 'fix', 'refactor', 'improve', 'explain',
  'show', 'review', 'check', 'what', 'does', 'how', 'where', 'which',
]);

export function detectLikelyCodeSymbol(message: string): boolean {
  return extractSymbolCandidates(message).length > 0;
}

function extractSymbolCandidates(message: string): string[] {
  const tokens = message.match(/\b[A-Za-z_$][A-Za-z0-9_$]{2,}\b/g) ?? [];
  return tokens.filter(t => {
    const lower = t.toLowerCase();
    if (STOP_WORDS.has(lower)) return false;
    if (/^\d/.test(t)) return false;
    return /[A-Z_]|[a-z][A-Z]/.test(t);
  });
}

// ── Intent detection ─────────────────────────────────────────────────────────

export function extractPathCandidates(message: string): string[] {
  const out = new Set<string>();
  for (const m of message.match(PATH_RE) ?? []) out.add(m);
  for (const m of message.match(new RegExp(FILE_EXT_RE.source, 'gi')) ?? []) out.add(m);
  return [...out];
}

export function decideRepoContextMode(
  message: string,
  resolvedPathCount: number
): RepoContextDecision {
  const lower = message.toLowerCase();
  const isEditIntent = EDIT_INTENT_WORDS.some(w => {
    const re = new RegExp(`\\b${w}\\b`, 'i');
    return re.test(lower);
  });
  const hasFileReference = detectFileReference(message);
  const hasSymbolReference = detectLikelyCodeSymbol(message);

  if (isEditIntent) {
    if (resolvedPathCount > 0) {
      return {
        mode: 'full_file_required',
        isEditIntent,
        hasFileReference,
        hasSymbolReference,
        reason: 'User appears to be requesting a code change with a resolvable target file.',
      };
    }
    return {
      mode: 'clarification_required',
      isEditIntent,
      hasFileReference,
      hasSymbolReference,
      reason: 'Edit intent detected but no target file could be resolved.',
    };
  }

  if (hasFileReference || hasSymbolReference) {
    return {
      mode: 'rag_plus_full_file',
      isEditIntent,
      hasFileReference,
      hasSymbolReference,
      reason: 'User mentioned a specific file path or code symbol.',
    };
  }

  return {
    mode: 'rag_only',
    isEditIntent,
    hasFileReference,
    hasSymbolReference,
    reason: 'General repository question.',
  };
}

// ── V1 Resolvers ───────────────────────────────────────────────────────────

export const explicitPathResolver: RepoResolver = {
  name: 'explicitPath',
  async resolve(input) {
    const candidates = extractPathCandidates(input.message);
    const out: ResolvedPathCandidate[] = [];
    for (const candidate of candidates) {
      const exact = input.repoTreePaths.find(p => p === candidate);
      if (exact) {
        out.push({ path: exact, source: 'explicit_path', confidence: 1.0 });
        continue;
      }
      const bySuffix = input.repoTreePaths.find(p => p.endsWith(`/${candidate}`) || p.endsWith(candidate));
      if (bySuffix) {
        out.push({ path: bySuffix, source: 'explicit_path', confidence: 0.95 });
      }
    }
    return out;
  },
};

export const filenameResolver: RepoResolver = {
  name: 'filename',
  async resolve(input) {
    const candidates = extractPathCandidates(input.message);
    const out: ResolvedPathCandidate[] = [];
    const lowerMsg = input.message.toLowerCase();

    for (const candidate of candidates) {
      const basename = candidate.includes('/') ? candidate.split('/').pop()! : candidate;
      const matches = input.repoTreePaths.filter(
        p => p.split('/').pop()?.toLowerCase() === basename.toLowerCase()
      );
      if (matches.length === 1) {
        out.push({ path: matches[0], source: 'filename', confidence: 0.85 });
      } else if (matches.length > 1 && lowerMsg.includes(basename.toLowerCase())) {
        for (const m of matches.slice(0, 2)) {
          out.push({ path: m, source: 'filename', confidence: 0.7 });
        }
      }
    }
    return out;
  },
};

export const symbolTextResolver: RepoResolver = {
  name: 'symbolText',
  async resolve(input) {
    const symbols = extractSymbolCandidates(input.message);
    if (symbols.length === 0) return [];

    const out: ResolvedPathCandidate[] = [];
    const seen = new Set<string>();

    for (const symbol of symbols.slice(0, 5)) {
      for (const chunk of input.ragResults) {
        if (chunk.file_path.startsWith('[')) continue;
        if (chunk.chunk_text.includes(symbol) && !seen.has(chunk.file_path)) {
          seen.add(chunk.file_path);
          out.push({ path: chunk.file_path, source: 'symbol_text', confidence: 0.8 });
        }
      }

      const userId = await getCurrentUserId();
      if (userId && isSupabaseConfigured) {
        const { data, error } = await supabase.rpc('search_github_repo_fts', {
          query_text: symbol,
          filter_user_id: userId,
          filter_owner: input.owner,
          filter_repo: input.repo,
          match_count: 5,
        });
        if (error) {
          console.warn('[Repo Router] FTS symbol search failed:', error.message);
        } else {
          for (const row of (data ?? []) as Array<{ file_path: string; chunk_text?: string }>) {
            const text = row.chunk_text ?? '';
            if (text.includes(symbol) && !seen.has(row.file_path)) {
              seen.add(row.file_path);
              out.push({ path: row.file_path, source: 'symbol_text', confidence: 0.75 });
            }
          }
        }
      }
    }
    return out;
  },
};

export const ragVectorResolver: RepoResolver = {
  name: 'ragVector',
  async resolve(input) {
    const lowerMsg = input.message.toLowerCase();
    const out: ResolvedPathCandidate[] = [];
    const seen = new Set<string>();

    for (const chunk of input.ragResults) {
      if (chunk.file_path.startsWith('[') || chunk.file_path === 'README.md') continue;
      const basename = chunk.file_path.split('/').pop()?.toLowerCase() ?? '';
      if (basename && lowerMsg.includes(basename) && !seen.has(chunk.file_path)) {
        seen.add(chunk.file_path);
        out.push({
          path: chunk.file_path,
          source: 'rag_vector',
          confidence: 0.5 + Math.min(chunk.similarity ?? 0, 0.4),
        });
      }
    }
    return out;
  },
};

/** V2 — deterministic symbol → file_path lookup via AST index. */
export const astSymbolResolver: RepoResolver = {
  name: 'astSymbol',
  async resolve(input) {
    const userId = await getCurrentUserId();
    if (!userId || !isSupabaseConfigured) return [];

    const symbolNames = extractSymbolCandidates(input.message);
    if (symbolNames.length === 0) return [];

    const { data, error } = await supabase.rpc('lookup_github_repo_symbols', {
      filter_user_id: userId,
      filter_owner: input.owner,
      filter_repo: input.repo,
      symbol_names: symbolNames,
    });

    if (error || !data) {
      if (error) console.warn('[Repo Router] lookup_github_repo_symbols failed:', error.message);
      return [];
    }

    const out: ResolvedPathCandidate[] = [];
    const seen = new Set<string>();
    for (const row of data as Array<{ file_path: string }>) {
      if (row.file_path && !seen.has(row.file_path)) {
        seen.add(row.file_path);
        out.push({ path: row.file_path, source: 'ast_symbol', confidence: 0.95 });
      }
    }
    return out;
  },
};

/** V3 — graph neighbor expansion (used via expandContextWithGraph, not resolver chain). */
export const graphResolver: RepoResolver = {
  name: 'graph',
  async resolve() {
    return [];
  },
};

export const DEFAULT_V2_RESOLVERS: RepoResolver[] = [
  explicitPathResolver,
  filenameResolver,
  astSymbolResolver,
  symbolTextResolver,
  ragVectorResolver,
];

export const DEFAULT_V1_RESOLVERS: RepoResolver[] = DEFAULT_V2_RESOLVERS;

// ── Path validation against repo tree ────────────────────────────────────────

/** Resolve a path to its canonical tree entry (exact or suffix match). */
export function resolveCanonicalRepoPath(path: string, repoTreePaths: string[]): string | null {
  if (repoTreePaths.length === 0) return path;
  if (repoTreePaths.includes(path)) return path;
  const bySuffix = repoTreePaths.find(p => p.endsWith(`/${path}`) || p.endsWith(path));
  return bySuffix ?? null;
}

/** Keep only paths that exist in the repo tree (when tree is available). */
export function filterPathsAgainstTree(
  paths: string[],
  repoTreePaths: string[],
  indexedPaths?: string[]
): string[] {
  const validationList =
    repoTreePaths.length > 0 ? repoTreePaths : indexedPaths?.length ? indexedPaths : null;
  if (!validationList) return paths;

  const validSet = new Set(validationList);
  const out: string[] = [];
  const seen = new Set<string>();
  for (const p of paths) {
    let canonical: string | null = null;
    if (validSet.has(p)) {
      canonical = p;
    } else if (repoTreePaths.length > 0) {
      canonical = resolveCanonicalRepoPath(p, repoTreePaths);
    } else {
      canonical = p;
    }
    if (canonical && !seen.has(canonical)) {
      seen.add(canonical);
      out.push(canonical);
    }
  }
  return out;
}

// ── Intent-based path relevance ──────────────────────────────────────────────

const RELEVANCE_STOP = new Set([
  ...STOP_WORDS,
  'want', 'wanna', 'try', 'again', 'hope', 'will', 'fixed', 'issue', 'direct', 'link',
  'sheet', 'google', 'upload', 'from', 'the', 'and', 'for', 'that', 'this', 'have',
]);

/** Score how well a repo path matches the user's message (0–1). */
export function scorePathRelevance(message: string, path: string): number {
  const lower = message.toLowerCase();
  const pathLower = path.toLowerCase();
  const basename = path.split('/').pop()?.replace(/\.[^.]+$/, '') ?? '';
  const normalizedBasename = basename
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[-_]/g, ' ')
    .toLowerCase();

  const terms =
    lower.match(/\b[a-z][a-z0-9_-]{2,}\b/g)?.filter(t => !RELEVANCE_STOP.has(t)) ?? [];

  let score = 0;
  for (const term of terms) {
    if (pathLower.includes(term)) score += 0.15;
    if (normalizedBasename.includes(term)) score += 0.25;
  }

  const msgCompact = lower.replace(/[^a-z0-9]/g, '');
  const baseCompact = normalizedBasename.replace(/\s+/g, '');
  if (baseCompact.length >= 4 && msgCompact.includes(baseCompact)) score += 0.45;

  if (/\bmusic\b/.test(lower) && pathLower.includes('music')) score += 0.2;
  if (/\bmanagement\b/.test(lower) && /management/i.test(basename)) score += 0.25;
  if (/\bpage\b/.test(lower) && pathLower.includes('/pages/')) score += 0.15;
  if (/\bmusic\b/.test(lower) && pathLower.includes('life-music')) score += 0.2;

  if (/\bmusic\b/.test(lower) && (pathLower.includes('spreadsheet') || pathLower.includes('prayerplan'))) {
    score -= 0.35;
  }
  if (/\bprayer\b/.test(lower) && pathLower.includes('prayer')) score += 0.15;

  return Math.max(0, Math.min(1, score));
}

export function rankCandidatesWithIntent(
  message: string,
  candidates: ResolvedPathCandidate[]
): ResolvedPathCandidate[] {
  return candidates
    .map(c => ({
      ...c,
      confidence: c.confidence * 0.55 + scorePathRelevance(message, c.path) * 0.45,
    }))
    .sort((a, b) => b.confidence - a.confidence);
}

export function pickPrimaryIntentPath(
  message: string,
  candidates: ResolvedPathCandidate[],
  resolvedPaths: string[]
): string | undefined {
  const pool = candidates.filter(
    c => c.source !== 'graph' && resolvedPaths.includes(c.path)
  );
  if (pool.length === 0) return resolvedPaths[0];
  const ranked = rankCandidatesWithIntent(message, pool);
  return ranked[0]?.path;
}

// ── Candidate merge ──────────────────────────────────────────────────────────

export function mergePathCandidates(candidates: ResolvedPathCandidate[], maxPaths = MAX_FULL_FILES): string[] {
  const byPath = new Map<string, ResolvedPathCandidate>();
  for (const c of candidates) {
    const existing = byPath.get(c.path);
    if (!existing || c.confidence > existing.confidence) {
      byPath.set(c.path, c);
    }
  }
  return [...byPath.values()]
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, maxPaths)
    .map(c => c.path);
}

export async function runResolverChain(
  input: RepoResolverInput,
  resolvers: RepoResolver[] = DEFAULT_V1_RESOLVERS
): Promise<ResolvedPathCandidate[]> {
  const all: ResolvedPathCandidate[] = [];
  for (const resolver of resolvers) {
    try {
      const found = await resolver.resolve(input);
      all.push(...found);
    } catch (err) {
      console.warn(`[Repo Router] Resolver ${resolver.name} failed:`, err);
    }
  }
  return all;
}

// ── Full-file fetch ──────────────────────────────────────────────────────────

export async function fetchResolvedFilesFromGitHub(opts: {
  owner: string;
  repo: string;
  paths: string[];
  ref?: string;
  silent?: boolean;
}): Promise<ResolvedFullFile[]> {
  const paths = opts.paths.slice(0, MAX_FULL_FILES);
  const results = await Promise.all(
    paths.map(async (path): Promise<ResolvedFullFile | null> => {
      try {
        const file = await githubService.getFileContent(opts.owner, opts.repo, path, opts.ref);
        const content = file.content ?? '';
        if (!content.trim()) return null;
        const byteSize = new TextEncoder().encode(content).length;
        if (byteSize > MAX_FULL_FILE_BYTES) {
          return {
            path,
            content: content.slice(0, MAX_FULL_FILE_BYTES),
            truncated: true,
          };
        }
        return { path, content, truncated: false };
      } catch (err) {
        if (!opts.silent) {
          console.warn(`[Repo Router] Failed to fetch ${path} from GitHub:`, err);
        }
        return null;
      }
    })
  );
  return results.filter((f): f is ResolvedFullFile => f !== null);
}

/** Index-first: use repo chunks when available; GitHub only for gaps (or when not fully indexed). */
export async function fetchResolvedFiles(opts: {
  owner: string;
  repo: string;
  paths: string[];
  ref?: string;
}): Promise<ResolvedFullFile[]> {
  const paths = opts.paths.slice(0, MAX_FULL_FILES);
  if (paths.length === 0) return [];

  const fromIndex = await reconstructFilesFromChunks(opts.owner, opts.repo, paths);
  const indexByPath = new Map(fromIndex.map(f => [f.path, f]));
  const indexFiles: ResolvedFullFile[] = fromIndex.map(f => ({ ...f, fromIndex: true }));

  const fullyIndexed = await isRepoFullyIndexed(opts.owner, opts.repo);
  if (fullyIndexed) {
    return indexFiles;
  }

  const missing = paths.filter(p => !indexByPath.has(p));
  if (missing.length === 0) return indexFiles;

  const fromGithub = await fetchResolvedFilesFromGitHub({
    ...opts,
    paths: missing,
    silent: true,
  });

  const seen = new Set(indexFiles.map(f => f.path));
  const merged = [...indexFiles];
  for (const f of fromGithub) {
    if (!seen.has(f.path)) {
      seen.add(f.path);
      merged.push(f);
    }
  }
  return merged;
}

/** V3 — expand context with import neighbors, callers, and co-located tests. */
export async function expandContextWithGraph(
  seedPaths: string[],
  owner: string,
  repo: string,
  repoTreePaths: string[],
  opts?: { maxFiles?: number }
): Promise<ResolvedPathCandidate[]> {
  if (seedPaths.length === 0) return [];
  const maxFiles = opts?.maxFiles ?? 3;
  try {
    const neighbors = await getGraphNeighborPaths(owner, repo, seedPaths, repoTreePaths);
    return neighbors.slice(0, maxFiles).map(path => ({
      path,
      source: 'graph' as const,
      confidence: 0.6,
    }));
  } catch (err) {
    console.warn('[Repo Router] expandContextWithGraph failed:', err);
    return [];
  }
}

// ── Orchestrator ─────────────────────────────────────────────────────────────

export async function buildRepoContext(
  input: RepoContextBuildInput,
  resolvers: RepoResolver[] = DEFAULT_V2_RESOLVERS
): Promise<RepoContextBuildResult> {
  const message = input.message.trim();
  const query = message || `${input.owner}/${input.repo}`;

  let ragResults: RepoChunkResult[] = [];
  try {
    ragResults = await searchRepo(query, input.owner, input.repo, {
      limit: 15,
      hybridSearch: true,
    });
  } catch (err) {
    console.warn('[Repo Router] searchRepo failed:', err);
  }

  const resolverInput: RepoResolverInput = {
    message,
    owner: input.owner,
    repo: input.repo,
    repoTreePaths: input.repoTreePaths,
    ragResults,
  };

  let candidates = await runResolverChain(resolverInput, resolvers);
  candidates = rankCandidatesWithIntent(message, candidates);
  let resolvedPaths = mergePathCandidates(candidates);

  let decision = decideRepoContextMode(message, resolvedPaths.length);

  if (decision.mode === 'full_file_required') {
    try {
      const moreRag = await searchRepo(query, input.owner, input.repo, {
        limit: 20,
        hybridSearch: true,
      });
      const mergedRag = [...ragResults];
      const seenKeys = new Set(ragResults.map(r => `${r.file_path}::${r.chunk_index}`));
      for (const r of moreRag) {
        const k = `${r.file_path}::${r.chunk_index}`;
        if (!seenKeys.has(k)) {
          seenKeys.add(k);
          mergedRag.push(r);
        }
      }
      ragResults = mergedRag;
      resolverInput.ragResults = ragResults;
      candidates = await runResolverChain(resolverInput, resolvers);
      candidates = rankCandidatesWithIntent(message, candidates);
      resolvedPaths = mergePathCandidates(candidates);
      decision = decideRepoContextMode(message, resolvedPaths.length);
    } catch (err) {
      console.warn('[Repo Router] Extended RAG search failed:', err);
    }
  }

  let fullFiles: ResolvedFullFile[] = [];
  let graphExpandedPaths: string[] = [];
  let primaryIntentPath: string | undefined;
  const shouldFetch =
    decision.mode === 'rag_plus_full_file' || decision.mode === 'full_file_required';

  candidates = rankCandidatesWithIntent(message, candidates);

  if (shouldFetch && resolvedPaths.length > 0) {
    const isEdit = decision.mode === 'full_file_required';

    if (isEdit) {
      const editCandidates = candidates.filter(c => c.source !== 'graph');
      const fetchPaths = filterPathsAgainstTree(
        mergePathCandidates(editCandidates, 2),
        input.repoTreePaths
      );
      resolvedPaths = fetchPaths;
      primaryIntentPath = pickPrimaryIntentPath(message, candidates, fetchPaths);

      fullFiles = await fetchResolvedFiles({
        owner: input.owner,
        repo: input.repo,
        paths: fetchPaths,
        ref: input.ref,
      });
    } else {
      const primaryBudget = Math.max(1, MAX_FULL_FILES - 2);
      const primaryPaths = mergePathCandidates(
        candidates.filter(c => c.source !== 'graph'),
        primaryBudget
      );

      const graphCandidates = await expandContextWithGraph(
        primaryPaths,
        input.owner,
        input.repo,
        input.repoTreePaths,
        { maxFiles: MAX_FULL_FILES - primaryPaths.length }
      );
      if (graphCandidates.length > 0) {
        candidates = [...candidates, ...graphCandidates];
        graphExpandedPaths = graphCandidates.map(c => c.path);
      }

      const fetchPaths = filterPathsAgainstTree(
        mergePathCandidates(candidates, MAX_FULL_FILES),
        input.repoTreePaths
      );
      resolvedPaths = fetchPaths;

      fullFiles = await fetchResolvedFiles({
        owner: input.owner,
        repo: input.repo,
        paths: fetchPaths,
        ref: input.ref,
      });
    }
  }

  if (!primaryIntentPath && resolvedPaths.length > 0) {
    primaryIntentPath = pickPrimaryIntentPath(message, candidates, resolvedPaths);
  }

  if (
    decision.mode === 'full_file_required' &&
    resolvedPaths.length > 0 &&
    fullFiles.length === 0
  ) {
    decision = {
      ...decision,
      mode: 'clarification_required',
      reason: 'Edit intent detected but full file could not be fetched from GitHub or repo index.',
    };
  }

  const allowRepoEdit =
    decision.mode === 'full_file_required' && fullFiles.length > 0;

  return {
    decision,
    ragResults,
    candidates,
    resolvedPaths,
    graphExpandedPaths,
    fullFiles,
    allowRepoEdit,
    primaryIntentPath,
  };
}

export function extractRepoTreePaths(cachedRepoContext: unknown): string[] {
  const ctx = cachedRepoContext as {
    tree?: { tree?: Array<{ path: string; type: string }> };
  } | null;
  const items = ctx?.tree?.tree;
  if (!Array.isArray(items)) return [];
  return items.filter(i => i.type === 'blob').map(i => i.path);
}
